import { useState, useCallback, useEffect } from 'react';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  uploadBytesResumable,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { doc, onSnapshot, collection, query, where, orderBy } from 'firebase/firestore';
import { storage, db } from '../utils/firebase';

export interface TranscodingJob {
  id: string;
  originalPath: string;
  originalFileName: string;
  fileType: 'image' | 'video' | 'audio' | 'document';
  mimeType: string;
  companyId: string;
  projectId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
  transcodedFiles: TranscodingResult[];
  createdAt: any;
  updatedAt: any;
}

export interface TranscodingResult {
  path: string;
  fileName: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  duration?: number;
  quality: 'original' | 'high' | 'medium' | 'low' | 'thumbnail';
}

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'transcoding' | 'completed' | 'error';
  error?: string;
  originalUrl?: string;
  thumbnailUrl?: string;
  transcodedFiles?: TranscodingResult[];
  transcodingJobId?: string;
}

function generateVideoThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    video.onloadeddata = () => {
      video.currentTime = Math.min(1, video.duration * 0.1);
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(objectUrl);
        video.remove();
        reject(new Error('Failed to get canvas 2d context'));
        return;
      }
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          video.remove();
          if (blob) resolve(blob);
          else reject(new Error('Failed to generate thumbnail'));
        },
        'image/jpeg',
        0.7
      );
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      video.remove();
      reject(new Error('Failed to load video for thumbnail'));
    };
  });
}

export interface UseFileUploadOptions {
  uploadPath?: string;
  onUploadComplete?: (result: UploadProgress) => void;
  onUploadError?: (error: Error, fileId: string) => void;
  onTranscodingComplete?: (result: UploadProgress) => void;
}

export const useFileUpload = (options: UseFileUploadOptions) => {
  const [uploads, setUploads] = useState<Map<string, UploadProgress>>(new Map());
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const allUploads = Array.from(uploads.values());
    const hasActiveUploads = allUploads.some((upload) => upload.status === 'uploading');
    setIsUploading(hasActiveUploads);
  }, [uploads]);

  const uploadFile = useCallback(
    async (file: File): Promise<string> => {

      if (!options.uploadPath) {
        throw new Error('Upload path is not defined. Please ensure a project is selected.');
      }

      const path = `${options.uploadPath}/${file.name}`;
      const fileId = path.split('/').pop() || '';

      const uploadProgress: UploadProgress = {
        fileId,
        fileName: file.name,
        progress: 0,
        status: 'uploading',
      };

      setUploads((prev) => new Map(prev.set(fileId, uploadProgress)));

      try {
        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Generate client-side thumbnail for videos (runs in parallel with upload)
        if (file.type.startsWith('video/')) {
          generateVideoThumbnail(file)
            .then(async (thumbBlob) => {
              const thumbPath = `${path}_thumb.jpg`;
              const thumbRef = ref(storage, thumbPath);
              await uploadBytes(thumbRef, thumbBlob);
              const thumbUrl = await getDownloadURL(thumbRef);
              setUploads((prev) => {
                const updated = new Map(prev);
                const current = updated.get(fileId);
                if (current) {
                  updated.set(fileId, { ...current, thumbnailUrl: thumbUrl });
                }
                return updated;
              });
            })
            .catch(() => {
              // Thumbnail generation failed — will use dark placeholder
            });
        }

        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploads((prev) => {
              const updated = new Map(prev);
              const current = updated.get(fileId);
              if (current) {
                updated.set(fileId, {
                  ...current,
                  progress: Math.round(progress),
                  status: progress === 100 ? 'transcoding' : 'uploading',
                });
              }
              return updated;
            });
          },
          (error) => {
            setUploads((prev) => {
              const updated = new Map(prev);
              const current = updated.get(fileId);
              if (current) {
                updated.set(fileId, {
                  ...current,
                  status: 'error',
                  error: error.message,
                });
              }
              return updated;
            });
            options.onUploadError?.(error, fileId);
            setIsUploading(false);
          },
          async () => {
            try {
              let downloadURL: string;

              try {
                downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              } catch (urlError) {
                const isUsingEmulators = process.env.REACT_APP_ENV === 'Development' && process.env.REACT_APP_USE_EMULATORS === 'true';

                if (isUsingEmulators) {
                  const bucket = uploadTask.snapshot.ref.bucket;
                  const fullPath = uploadTask.snapshot.ref.fullPath;
                  const encodedPath = encodeURIComponent(fullPath);
                  downloadURL = `http://${process.env.REACT_APP_LOCAL_IP || '127.0.0.1'}:9199/v0/b/${bucket}/o/${encodedPath}?alt=media`;
                } else {
                  const bucket = uploadTask.snapshot.ref.bucket;
                  const fullPath = uploadTask.snapshot.ref.fullPath;
                  downloadURL = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(fullPath)}?alt=media`;
                }
              }

              setUploads((prev) => {
                const updated = new Map(prev);
                const current = updated.get(fileId);
                if (current) {
                  updated.set(fileId, {
                    ...current,
                    originalUrl: downloadURL,
                    status: 'completed',
                    progress: 100,
                  });
                }
                return updated;
              });

              const updatedUpload = {
                fileId,
                fileName: uploadProgress.fileName,
                progress: 100,
                status: 'completed' as const,
                originalUrl: downloadURL,
              };

              options.onUploadComplete?.(updatedUpload);

              const allUploads = Array.from(uploads.values());
              const allCompleted = allUploads.every((upload) =>
                upload.status === 'completed' || upload.status === 'error'
              );

              if (allCompleted) {
                setIsUploading(false);
              }
            } catch (error) {
              setUploads((prev) => {
                const updated = new Map(prev);
                const current = updated.get(fileId);
                if (current) {
                  updated.set(fileId, {
                    ...current,
                    status: 'error',
                    error: error instanceof Error ? error.message : 'Unknown error',
                  });
                }
                return updated;
              });
              options.onUploadError?.(error as Error, fileId);
            }
          }
        );

        return fileId;
      } catch (error) {
        setUploads((prev) => {
          const updated = new Map(prev);
          const current = updated.get(fileId);
          if (current) {
            updated.set(fileId, {
              ...current,
              status: 'error',
              error: error instanceof Error ? error.message : 'Upload failed',
            });
          }
          return updated;
        });
        options.onUploadError?.(error as Error, fileId);
        setIsUploading(false);
        throw error;
      }
    },
    [options]
  );

  const uploadMultipleFiles = useCallback(
    async (files: File[]): Promise<string[]> => {
      const fileIds: string[] = [];

      for (const file of files) {
        try {
          const fileId = await uploadFile(file);
          fileIds.push(fileId);
        } catch {
          // Individual file upload failed — continues with remaining files
        }
      }

      return fileIds;
    },
    [uploadFile]
  );

  const getUploadProgress = useCallback(
    (fileId: string): UploadProgress | undefined => {
      return uploads.get(fileId);
    },
    [uploads]
  );

  const getAllUploads = useCallback((): UploadProgress[] => {
    return Array.from(uploads.values());
  }, [uploads]);

  const clearUpload = useCallback((fileId: string) => {
    setUploads((prev) => {
      const updated = new Map(prev);
      updated.delete(fileId);
      return updated;
    });
  }, []);

  const clearAllUploads = useCallback(() => {
    setUploads(new Map());
  }, []);

  return {
    uploadFile,
    uploadMultipleFiles,
    getUploadProgress,
    getAllUploads,
    clearUpload,
    clearAllUploads,
    isUploading,
    uploads: getAllUploads(),
  };
};

export const useTranscodingProgress = (fileId: string) => {
  const [transcodingJob, setTranscodingJob] = useState<TranscodingJob | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setIsLoading(false);
      return;
    }

    const q = query(
      collection(db, 'transcodingJobs'),
      where('originalPath', '==', `companies/*/projects/*/posts/${fileId}`),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          setTranscodingJob(null);
          setIsLoading(false);
          return;
        }

        const job = snapshot.docs[0].data() as TranscodingJob;
        setTranscodingJob(job);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [fileId]);

  return {
    transcodingJob,
    isLoading,
    error,
    isTranscoding: transcodingJob?.status === 'processing',
    isCompleted: transcodingJob?.status === 'completed',
    isFailed: transcodingJob?.status === 'failed',
    progress: transcodingJob?.progress || 0,
  };
};

export const useTranscodingProgressForFiles = (fileIds: string[]) => {
  const [transcodingJobs, setTranscodingJobs] = useState<Map<string, TranscodingJob>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!fileIds.length) {
      setIsLoading(false);
      return;
    }

    const unsubscribeFunctions: (() => void)[] = [];

    fileIds.forEach((fileId) => {
      const q = query(
        collection(db, 'transcodingJobs'),
        where('originalPath', '==', `companies/*/projects/*/posts/${fileId}`),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (!snapshot.empty) {
            const job = snapshot.docs[0].data() as TranscodingJob;
            setTranscodingJobs((prev) => {
              const updated = new Map(prev);
              updated.set(fileId, job);
              return updated;
            });
          }
          setIsLoading(false);
        },
        (err) => {
          setIsLoading(false);
        }
      );

      unsubscribeFunctions.push(unsubscribe);
    });

    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    };
  }, [fileIds]);

  const getTranscodingJob = useCallback(
    (fileId: string): TranscodingJob | undefined => {
      return transcodingJobs.get(fileId);
    },
    [transcodingJobs]
  );

  const isTranscoding = useCallback(
    (fileId: string): boolean => {
      const job = transcodingJobs.get(fileId);
      return job?.status === 'processing';
    },
    [transcodingJobs]
  );

  const isCompleted = useCallback(
    (fileId: string): boolean => {
      const job = transcodingJobs.get(fileId);
      return job?.status === 'completed';
    },
    [transcodingJobs]
  );

  const getProgress = useCallback(
    (fileId: string): number => {
      const job = transcodingJobs.get(fileId);
      return job?.progress || 0;
    },
    [transcodingJobs]
  );

  return {
    transcodingJobs,
    isLoading,
    getTranscodingJob,
    isTranscoding,
    isCompleted,
    getProgress,
  };
};

export const useTranscodedFiles = (companyId: string, projectId: string) => {
  const [transcodedFiles, setTranscodedFiles] = useState<TranscodingJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId || !projectId) {
      setIsLoading(false);
      return;
    }

    const q = query(
      collection(db, 'transcodingJobs'),
      where('companyId', '==', companyId),
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const jobs = snapshot.docs.map((doc) => doc.data() as TranscodingJob);
        setTranscodedFiles(jobs);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, projectId]);

  const getTranscodedFile = useCallback(
    (originalFileName: string) => {
      return transcodedFiles.find(
        (job) => job.originalFileName === originalFileName && job.status === 'completed'
      );
    },
    [transcodedFiles]
  );

  const getTranscodedFilesByType = useCallback(
    (fileType: 'image' | 'video' | 'audio' | 'document') => {
      return transcodedFiles.filter(
        (job) => job.fileType === fileType && job.status === 'completed'
      );
    },
    [transcodedFiles]
  );

  return {
    transcodedFiles,
    isLoading,
    error,
    getTranscodedFile,
    getTranscodedFilesByType,
  };
};
